import React, { forwardRef, useState } from 'react';
import clsx from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

// Глазик-переключатель видимости для password-полей. Кастомный <input> не
// даёт нативной кнопки «показать пароль» (а выделять точки бессмысленно —
// получался «зачёркнутый белым» текст). Для type="password" рисуем кнопку
// справа, которая переключает реальный type между password/text.
const EyeIcon: React.FC<{ off?: boolean }> = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
    {off && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className, id, type = 'text', ...props }, ref) => {
    const inputId = id || props.name || label;
    const isPassword = type === 'password';
    const [reveal, setReveal] = useState(false);
    const effectiveType = isPassword ? (reveal ? 'text' : 'password') : type;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-sm font-medium text-zinc-400"
          >
            {label}
            {props.required && <span className="ml-1 text-danger">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={effectiveType}
            className={clsx(
              'block w-full rounded-md border bg-bg-card px-3.5 py-2.5 text-sm text-fg-primary shadow-sm placeholder:text-fg-muted transition-colors duration-200 focus:outline-none focus:ring-2 min-h-[44px]',
              isPassword && 'pr-11',
              error
                ? 'border-danger focus:border-danger focus:ring-danger/25'
                : 'border-line focus:border-accent focus:ring-[color:var(--focus-ring)]',
              className,
            )}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              tabIndex={-1}
              aria-label={reveal ? 'Скрыть пароль' : 'Показать пароль'}
              title={reveal ? 'Скрыть пароль' : 'Показать пароль'}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-fg-muted transition-colors hover:text-fg-primary"
            >
              <EyeIcon off={reveal} />
            </button>
          )}
        </div>
        {error && (
          <p className="mt-1.5 text-xs text-danger">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-xs text-zinc-500">{helperText}</p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
